
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface TranslationSettings {
  id: string;
  is_active: boolean;
  default_language: string;
  active_languages: string[];
  widget_position: string;
  auto_detect_language: boolean;
}

interface TranslationContextType {
  currentLanguage: string;
  setCurrentLanguage: (lang: string) => void;
  translateText: (text: string) => Promise<string>;
  settings: TranslationSettings | null;
  isLoading: boolean;
  availableLanguages: { code: string; name: string; flag: string }[];
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const LANGUAGES = [
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }
];

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>('es');
  const [settings, setSettings] = useState<TranslationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Cache para traducciones
  const translationCache = new Map<string, string>();

  useEffect(() => {
    loadSettings();
    loadLanguageFromStorage();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('translation_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading translation settings:', error);
        return;
      }

      if (data) {
        setSettings(data);
        if (!currentLanguage || currentLanguage === 'es') {
          setCurrentLanguage(data.default_language);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadLanguageFromStorage = () => {
    const savedLanguage = localStorage.getItem('tecnosync-language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  };

  const changeLanguage = (lang: string) => {
    setCurrentLanguage(lang);
    localStorage.setItem('tecnosync-language', lang);
  };

  const translateText = async (text: string): Promise<string> => {
    if (!settings?.is_active || currentLanguage === settings.default_language) {
      return text;
    }

    const cacheKey = `${text}-${currentLanguage}`;
    
    // Verificar cache en memoria
    if (translationCache.has(cacheKey)) {
      console.log('Usando cache en memoria para:', text);
      return translationCache.get(cacheKey)!;
    }

    try {
      // 1. Buscar en diccionario personalizado con mejor matching
      console.log('Buscando en diccionario personalizado:', text);
      const { data: dictData, error: dictError } = await supabase
        .from('translation_dictionary')
        .select('translated_text')
        .eq('original_text', text.trim())
        .eq('target_language', currentLanguage)
        .eq('is_active', true)
        .maybeSingle();

      if (dictError) {
        console.error('Error en diccionario:', dictError);
      }

      if (dictData) {
        console.log('Encontrado en diccionario:', dictData.translated_text);
        translationCache.set(cacheKey, dictData.translated_text);
        return dictData.translated_text;
      }

      // 2. Buscar en cache de traducciones
      console.log('Buscando en cache de traducciones:', text);
      const { data: cacheData } = await supabase
        .from('translation_cache')
        .select('translated_text')
        .eq('original_text', text.trim())
        .eq('target_language', currentLanguage)
        .maybeSingle();

      if (cacheData) {
        console.log('Encontrado en cache:', cacheData.translated_text);
        translationCache.set(cacheKey, cacheData.translated_text);
        return cacheData.translated_text;
      }

      // 3. Solicitar traducción a API externa
      console.log('Solicitando traducción a API externa:', text);
      const translatedText = await requestTranslation(text, currentLanguage);
      
      if (translatedText && translatedText !== text) {
        // Guardar en cache de base de datos
        await supabase
          .from('translation_cache')
          .upsert({
            original_text: text.trim(),
            translated_text: translatedText,
            source_language: settings.default_language,
            target_language: currentLanguage,
            service_used: 'mymemory'
          });

        translationCache.set(cacheKey, translatedText);
        console.log('Traducción guardada en cache:', translatedText);
        return translatedText;
      }

    } catch (error) {
      console.error('Translation error:', error);
    }

    console.log('Retornando texto original:', text);
    return text;
  };

  const requestTranslation = async (text: string, targetLang: string): Promise<string> => {
    try {
      // Usar MyMemory API (gratuita)
      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${settings?.default_language}|${targetLang}`
      );
      
      const data = await response.json();
      
      if (data.responseStatus === 200 && data.responseData?.translatedText) {
        return data.responseData.translatedText;
      }
    } catch (error) {
      console.error('MyMemory API error:', error);
    }

    return text;
  };

  const availableLanguages = LANGUAGES.filter(lang => 
    settings?.active_languages?.includes(lang.code) || lang.code === settings?.default_language
  );

  return (
    <TranslationContext.Provider value={{
      currentLanguage,
      setCurrentLanguage: changeLanguage,
      translateText,
      settings,
      isLoading,
      availableLanguages
    }}>
      {children}
    </TranslationContext.Provider>
  );
};

const defaultContext: TranslationContextType = {
  currentLanguage: 'es',
  setCurrentLanguage: () => {},
  translateText: async (text: string) => text,
  settings: null,
  isLoading: false,
  availableLanguages: [],
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    console.warn('useTranslation used outside TranslationProvider, using defaults');
    return defaultContext;
  }
  return context;
};
